import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RotatingLines } from 'react-loader-spinner';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useTranslation } from 'react-i18next';

import { Input } from '../../../../components/common-inputs';
import { ICONS } from '../../../../icons';
import CommonButton from '../../../../components/common-button/CommonButton';
import Icon from '../../../../icons/Icon';
import validate from '../../../../utils/validate';
import WithError from '../../../../components/with-error/WithError';

import scss from './DemoUsersTable.module.scss';

function EditableCell({ value, onSave, children, validationSchema }) {
  const { t } = useTranslation('userManagement');

  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState(undefined);

  const handleSave = useCallback(async () => {
    const errors = await validate({ value: localValue }, validationSchema);
    if (errors) {
      setErrors(errors);
      return;
    }

    if (localValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    await onSave(localValue);
    setIsSaving(false);
    setIsEditing(false);
  }, [localValue, value, onSave, validationSchema]);

  const buttonConfig = useMemo(() => {
    if (isSaving) {
      return {
        icon: <RotatingLines height={15} width={15} strokeColor='#fdbf47' />,
        onClick: () => null,
        disabled: true,
      };
    }

    if (isEditing) {
      return {
        icon: <SaveOutlinedIcon />,
        onClick: handleSave,
        disabled: false,
      };
    }

    return {
      icon: <Icon icon={ICONS.Edit} />,
      onClick: () => setIsEditing(true),
      disabled: false,
    };
  }, [isSaving, isEditing, handleSave]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (!isEditing) {
      setErrors(undefined);
    }
  }, [isEditing]);

  return (
    <div className={scss.editableCell}>
      {isEditing ? (
        <WithError isShowError={errors} errorText={t(errors?.value)} isAbsolute>
          <Input value={localValue} onChange={setLocalValue} size='sm' />
        </WithError>
      ) : (
        children
      )}
      <CommonButton
        styleColor='secondary'
        onClick={buttonConfig.onClick}
        disabled={buttonConfig.disabled}
        maxWidth={36}>
        {buttonConfig.icon}
      </CommonButton>
    </div>
  );
}

export default EditableCell;
